import type React from 'react';
import { type MutableRefObject, type ReactElement, type Ref, useCallback, useMemo } from 'react';

import {
  type EntryStatusEnum,
  EventTypeEnum,
  ParticipantTypeEnum,
  StageTypeEnum,
} from '@clubspark-node/clubspark-tennis-types';
import { Box } from '@mui/material';
import { type Column, createColumnHelper } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { entryStatusConstants } from 'tods-competition-factory';

import { eventPlacedParticipantsAtomFamily } from 'atoms/events';
import TMXInput from 'components/inputs/TMXInput';
import Participant from 'components/participants/Participant';
import StandardActions from 'components/tables/actions/StandardActions';
import { EventChip } from 'components/tables/common/EventChip';
import { EventPlayerStatus } from 'components/tables/common/EventPlayerStatus';
import TableDragHandleCell from 'components/tables/common/TableDragHandleCell';
import TeamLink from 'components/tables/common/TeamLink';
import { actionsHeaderStyles, useStyles as useTableStyles } from 'components/tables/enhancedTable/styles';
import { getSelectionColumnDefinition } from 'components/tables/enhancedTable/utils';
import { BodyRegular, TableHeading } from 'components/typography/Typography';
import { getDrawRoute } from 'config/router/utils';
import { playerStatus } from 'constants/playerStatus';
import { type ParticipantOrId } from 'containers/dialogs/ChoosePlayer';
import useScalings from 'hooks/scalings/useScalings';
import { type EventInterface, type FlightProfile, type ParticipantInterface } from 'typedefs/store/tmxTypes';
import {
  entryRowSort,
  getDoublesAddress,
  getDoublesSectionAndDistrict,
  getPersonNameFromParticipant,
} from 'utils/participant';
import { type ScalingType, type SubScalingType } from 'utils/scalings';

import { DoublesDataDisplay } from './DoublesDataDisplay';
import { renderDraws } from './utils';

const columnHelper = createColumnHelper<ParticipantInterface>();

const SECTION = 'ustaSection';
const DISTRICT = 'ustaDistrict';
const { MAIN, QUALIFYING } = StageTypeEnum;

interface CompetitorTableColumnsInterface {
  classes: any;
  event: EventInterface;
  flights?: FlightProfile[];
  isEditReorder?: boolean;
  isInEditMode?: boolean;
  isTeamTable?: boolean;
  manualSeedRefs?: MutableRefObject<Record<string, Ref<HTMLInputElement>>>;
  onChangeSeeding?: (participantId: string, seedValue: string) => void;
  onOpenPlayerProfile?: (participants: ParticipantOrId[]) => Promise<void>;
  onClickAction?: any;
  requiredColumns: string[];
  seeds?: any[];
  selectedEntries?: any;
  isSelectedTable?: boolean;
}

const DrawItem: React.FC<
  React.PropsWithChildren<{ tournamentId: string; eventId: string; drawId: string; children: string }>
> = ({ tournamentId, eventId, drawId, children }) => (
  <EventChip href={!!tournamentId && getDrawRoute(tournamentId, eventId, drawId)} name={children} />
);

const emptyArray = [];
export const useCompetitorsTableColumns = ({
  classes,
  event,
  flights = emptyArray,
  isEditReorder,
  isInEditMode,
  isTeamTable,
  manualSeedRefs,
  onChangeSeeding,
  onOpenPlayerProfile,
  onClickAction,
  requiredColumns,
  seeds,
  isSelectedTable,
}: CompetitorTableColumnsInterface): Column<ParticipantInterface>[] => {
  const { t } = useTranslation();
  const { classes: tableClasses } = useTableStyles();
  const eventId = event?.eventId;
  const tournamentId = event?.tournamentId;
  const isSplitDraw = flights?.length > 1;
  const dragTableHeaderClass = `${!isEditReorder ? classes.dragTableHeaderCellDisabled + ' ' : ''}${
    classes.dragTableHeaderCell
  }`;
  const mainPlacedParticipantParams: {
    eventId: string;
    structureStage: StageTypeEnum.MAIN;
  } = useMemo(() => ({ eventId, structureStage: MAIN }), [eventId]);

  const qualifyingPlacedParticipantParams: {
    eventId: string;
    structureStage: StageTypeEnum.QUALIFYING;
  } = useMemo(() => ({ eventId, structureStage: QUALIFYING }), [eventId]);

  const mainPlacedParticipants = useAtomValue(eventPlacedParticipantsAtomFamily(mainPlacedParticipantParams));
  const qualifyingPlacedParticipants = useAtomValue(
    eventPlacedParticipantsAtomFamily(qualifyingPlacedParticipantParams),
  );

  const allPlacedParticipants = useMemo(
    () => [...mainPlacedParticipants, ...qualifyingPlacedParticipants],
    [mainPlacedParticipants, qualifyingPlacedParticipants],
  );

  const {
    availableScalingTypes,
    getValue: getScaleValue,
    getDisplayValue,
    getSortMethod,
    getDisplayName,
    getSubScalings,
  } = useScalings({
    event,
  });

  const getEntryStatus = useCallback(
    (participant: ParticipantInterface, eventId: string): EntryStatusEnum => {
      const playerEvent = participant?.events?.find((e) => e.eventId === eventId);
      if (isSelectedTable) {
        const participantEntry = playerEvent?.entries?.find((e) => e.participantId === participant?.participantId);
        const { entryStatus, entryStage } = participantEntry || {};
        return entryStage === QUALIFYING ? entryStatusConstants.QUALIFIER : entryStatus;
      }
      return playerEvent?.['entryStatus'];
    },
    [isSelectedTable],
  );

  const columns = useMemo(
    () => [
      columnHelper.display(getSelectionColumnDefinition()),
      columnHelper.accessor((participant, index) => index + 1, {
        id: 'index',
        header: '#',
        cell: ({ getValue, row, table: { getSortedRowModel } }) => {
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              {isEditReorder && <TableDragHandleCell className={dragTableHeaderClass} />}
              <BodyRegular>
                {isEditReorder !== false
                  ? getSortedRowModel().flatRows.findIndex((sortedFlatRow) => sortedFlatRow.id === row.id) + 1
                  : getValue()}
              </BodyRegular>
            </Box>
          );
        },
      }),
      columnHelper.accessor(
        (participant) => {
          return (
            (participant.participantType === ParticipantTypeEnum.TEAM || !participant.individualParticipants?.length
              ? [participant]
              : participant.individualParticipants
            )
              .map((p) =>
                p.participantType === ParticipantTypeEnum.TEAM || !p.individualParticipantIds?.length
                  ? p.participantName
                  : getPersonNameFromParticipant(participant),
              )
              .join(', ') || '-'
          );
        },
        {
          id: 'name',
          header: isTeamTable ? t('team') : t('nm'),
          cell: ({ row }) => {
            const isDoubles = event?.eventType === EventTypeEnum.DOUBLES;
            const participants =
              row.original.participantType === ParticipantTypeEnum.TEAM || !row.original.individualParticipants?.length
                ? [row.original]
                : row.original.individualParticipants;
            return (
              participants?.map((participant) =>
                participant.participantType === ParticipantTypeEnum.TEAM ? (
                  <TeamLink
                    key={participant.participantId}
                    name={participant.participantName}
                    participantId={participant.participantId}
                    tournamentId={tournamentId}
                  />
                ) : (
                  <Participant
                    key={participant.participantId}
                    isDoubles={isDoubles}
                    participantId={participant.participantId}
                    showAdditionalInfo={true}
                    onClick={(e, player) =>
                      onOpenPlayerProfile([
                        {
                          participantId: player.id,
                          participantName: player.name,
                        },
                      ])
                    }
                  />
                ),
              ) ?? <BodyRegular>{t('notApplicable')}</BodyRegular>
            );
          },
        },
      ),
      columnHelper.accessor((participant) => participant?.individualParticipants?.length || 0, {
        id: 'players',
        header: t('pyr'),
        cell: ({ getValue }) => <BodyRegular>{getValue()}</BodyRegular>,
      }),
      ...availableScalingTypes
        .flatMap<[ScalingType | SubScalingType, ScalingType]>(
          ([scalingType]) =>
            getSubScalings(scalingType)?.map((subScaling: SubScalingType): [SubScalingType, ScalingType] => [
              subScaling,
              scalingType,
            ]) || [[scalingType, scalingType]],
        )
        .map(([subScalingType, scalingType]) => {
          return columnHelper.accessor((participant) => getScaleValue(scalingType, participant, subScalingType), {
            id: `scaling_${subScalingType.toLowerCase()}`,
            header: getDisplayName(scalingType, subScalingType),
            cell: ({ row }) => {
              return getDisplayValue(scalingType, row.original, subScalingType) as ReactElement;
            },
            sortDescFirst: true,
            invertSorting: true,
            sortingFn: (rowA, rowB) =>
              getSortMethod(scalingType, 'TABLE_SORT', subScalingType)(rowA.original, rowB.original),
            enableColumnFilter: false,
          });
        }),
      columnHelper.accessor((participant) => participant?.events?.find((e) => e.eventId === eventId), {
        id: 'drawStatus',
        header: t('drawStatus'),
        cell: ({ getValue }) => <BodyRegular>{getValue()?.entryStatus}</BodyRegular>,
      }),
      columnHelper.accessor(
        (participant) => {
          if (participant.participantType === 'PAIR') return getDoublesAddress(participant)?.join(',') || '-';
          const { city, state } = participant.person?.addresses?.[0] || {};
          return [city, state].filter((f) => f).join(', ') || '-';
        },
        {
          id: 'cityState',
          header: () => (
            <TableHeading>
              {t('cty')}, {t('state')}
            </TableHeading>
          ),
          cell: ({ getValue, row }) =>
            row.original.participantType !== 'PAIR' ? (
              <BodyRegular>{getValue()}</BodyRegular>
            ) : (
              <DoublesDataDisplay data={getDoublesAddress(row.original)} />
            ),
          enableSorting: false,
        },
      ),
      columnHelper.accessor(
        (participant) => {
          if (participant.participantType === 'PAIR')
            return (getDoublesSectionAndDistrict(participant, DISTRICT)?.join(',') || '-') as string;

          return (participant.person?.extensions?.find((e) => e.name === DISTRICT)?.value?.name || '-') as string;
        },
        {
          id: 'district',
          header: t('district'),
          cell: ({ getValue, row }) =>
            row.original.participantType !== 'PAIR' ? (
              <BodyRegular>{getValue()}</BodyRegular>
            ) : (
              <DoublesDataDisplay data={getDoublesSectionAndDistrict(row.original, DISTRICT)} />
            ),
          enableSorting: false,
        },
      ),
      columnHelper.accessor(
        (participant) => {
          if (participant.participantType === 'PAIR')
            return (getDoublesSectionAndDistrict(participant, SECTION)?.join(',') || '-') as string;
          return (participant.person?.extensions?.find((e) => e.name === SECTION)?.value?.name || '-') as string;
        },
        {
          id: 'section',
          header: t('section'),
          cell: ({ getValue, row }) =>
            row.original.participantType !== 'PAIR' ? (
              <BodyRegular>{getValue()}</BodyRegular>
            ) : (
              <DoublesDataDisplay data={getDoublesSectionAndDistrict(row.original, SECTION)} />
            ),
          enableSorting: false,
        },
      ),
      columnHelper.accessor((participant) => getEntryStatus(participant, eventId), {
        id: 'player_status',
        header: t('status'),
        sortingFn: ({ original: a }, { original: b }) =>
          entryRowSort(
            playerStatus[getEntryStatus(a, eventId)]?.abbrv,
            playerStatus[getEntryStatus(b, eventId)]?.abbrv,
          ),
        cell: ({ getValue }) => <EventPlayerStatus short status={getValue() as keyof typeof playerStatus} />,
        sortDescFirst: true,
      }),
      isSplitDraw
        ? columnHelper.accessor(
            (participant) => {
              const draws = renderDraws(flights, participant);
              return draws.map((draw) => draw.drawName).join(', ');
            },
            {
              id: 'draws',
              header: t('drz'),
              cell: ({ row }) => {
                const participant = row.original;
                const draws = renderDraws(flights, participant);
                return draws?.map((draw) => (
                  <DrawItem key={draw.drawId} drawId={draw.drawId} eventId={eventId} tournamentId={tournamentId}>
                    {draw.drawName || 'Flight'}
                  </DrawItem>
                ));
              },
              enableSorting: false,
            },
          )
        : columnHelper.accessor(
            (participant) => {
              const seed = seeds?.find((seed) => seed.participantId === participant.participantId)?.scaleItem
                ?.scaleValue;
              return `${seed ?? ''}`;
            },
            {
              id: 'seed',
              header: t('seed'),
              sortDescFirst: true,
              invertSorting: true,
              sortingFn: (a, b) => {
                const seedA =
                  seeds
                    ?.find((seed) => seed.participantId === a.original.participantId)
                    ?.scaleItem?.scaleValue?.toString() ?? '';
                const seedB =
                  seeds
                    ?.find((seed) => seed.participantId === b.original.participantId)
                    ?.scaleItem?.scaleValue?.toString() ?? '';
                if (seedA === '') return 1;
                if (seedB === '') return -1;
                return seedA.localeCompare(seedB, 'en', { numeric: true });
              },
              cell: ({ row, getValue }) => {
                const handleOnChange = (event) => onChangeSeeding(row.id, event.target.value);
                return isInEditMode ? (
                  <TMXInput ref={manualSeedRefs?.current?.[row.original.participantId]} onChange={handleOnChange} />
                ) : (
                  <BodyRegular>{getValue() || '-'}</BodyRegular>
                );
              },
            },
          ),
      columnHelper.display({
        id: 'options',
        header: t('acts'),
        cell: ({ row }) =>
          !allPlacedParticipants.some((participant) => participant?.participantId === row.id) && (
            <StandardActions
              className={classes.actions}
              rowItem={row.original}
              onClick={(event) => onClickAction(row.original, event)}
            />
          ),
        enableSorting: false,
        meta: {
          headerClasses: [actionsHeaderStyles().className],
          cellClasses: [tableClasses?.actionsCell],
        },
      }),
    ],
    [
      isTeamTable,
      t,
      availableScalingTypes,
      isSplitDraw,
      tableClasses?.actionsCell,
      isEditReorder,
      dragTableHeaderClass,
      event?.eventType,
      tournamentId,
      onOpenPlayerProfile,
      getSubScalings,
      getDisplayName,
      getScaleValue,
      getDisplayValue,
      getSortMethod,
      eventId,
      getEntryStatus,
      flights,
      seeds,
      isInEditMode,
      manualSeedRefs,
      onChangeSeeding,
      allPlacedParticipants,
      classes.actions,
      onClickAction,
    ],
  );

  return useMemo(
    () =>
      columns
        .filter((c) => requiredColumns.includes(c.id))
        .sort(
          (a, b) => requiredColumns.indexOf(a.id) - requiredColumns.indexOf(b.id),
        ) as Column<ParticipantInterface>[],
    [columns, requiredColumns],
  );
};
